<div class="viewer-panel__content">
  <div class="viewer-panel__tree">
    <div class="viewer-panel__subtitle">Scene Tree</div>
    <div *ngIf="viewer.sceneTreeLoading" class="viewer-panel__hint">Loading tree...</div>
    <div *ngIf="!viewer.sceneTreeLoading && viewer.sceneTreeRoots.length === 0" class="viewer-panel__hint">
      No root nodes loaded.
    </div>

    <ng-container *ngFor="let rootNode of viewer.sceneTreeRoots">
      <ng-container *ngTemplateOutlet="sceneTreeNodeTemplate; context: { $implicit: rootNode }"></ng-container>
    </ng-container>
  </div>

  <div class="viewer-panel__properties">
    <div class="viewer-panel__subtitle">Node Properties</div>
    <div class="viewer-panel__state">Selected node: {{ viewer.selectedSceneNodeTitle ?? 'none' }}</div>
    <div class="viewer-panel__state">Visibility: {{ viewer.selectedSceneNodeVisibility }}</div>
    <div class="viewer-panel__state">
      3D focus bounds: {{ viewer.selectedSceneNodeId === null ? 'not loaded' : 'loaded (no overlay)' }}
    </div>
    <div *ngIf="viewer.selectedSceneNodeLoading" class="viewer-panel__hint">Loading node details...</div>
    <div
      *ngIf="!viewer.selectedSceneNodeLoading && viewer.selectedSceneNodeAttributes.length === 0"
      class="viewer-panel__hint"
    >
      No attributes to display.
    </div>

    <div class="viewer-panel__attributes" *ngIf="viewer.selectedSceneNodeAttributes.length > 0">
      <div class="viewer-panel__attribute" *ngFor="let attribute of viewer.selectedSceneNodeAttributes">
        <div class="viewer-panel__attribute-name">{{ attribute.groupName }} / {{ attribute.name }}</div>
        <div class="viewer-panel__attribute-value">{{ attribute.value }} {{ attribute.unit }}</div>
      </div>
    </div>

    <div class="viewer-panel__enterprise">
      <div class="viewer-panel__subtitle">Enterprise Metadata Linking</div>
      <div class="viewer-panel__state">{{ viewer.enterpriseFilterMessage }}</div>

      <label class="viewer-panel__control-label" for="enterpriseAttributeName">Attribute name</label>
      <input
        id="enterpriseAttributeName"
        class="viewer-panel__control-input"
        type="text"
        [value]="viewer.enterpriseAttributeName"
        (input)="viewer.onEnterpriseAttributeNameChanged($event)"
        placeholder="e.g. IFC_GUID, System, Zone"
      />

      <label class="viewer-panel__control-label" for="enterpriseAttributeValue">Attribute value</label>
      <input
        id="enterpriseAttributeValue"
        class="viewer-panel__control-input"
        type="text"
        list="enterpriseAttributeValuesList"
        [value]="viewer.enterpriseAttributeValue"
        (input)="viewer.onEnterpriseAttributeValueChanged($event)"
        placeholder="contains..."
      />

      <datalist id="enterpriseAttributeValuesList">
        <option *ngFor="let value of viewer.enterpriseAttributeValues" [value]="value"></option>
      </datalist>

      <div class="viewer-panel__actions">
        <input
          type="button"
          class="demo-button viewer-panel__button"
          value="Load Values"
          [disabled]="viewer.ingestState.sceneId === null || viewer.enterpriseAttributeName.trim().length === 0"
          (tap)="viewer.loadEnterpriseAttributeValues()"
        />
        <input
          type="button"
          class="demo-button viewer-panel__button"
          value="Search Nodes"
          [disabled]="viewer.ingestState.sceneId === null || viewer.enterpriseFilterLoading"
          (tap)="viewer.runEnterpriseMetadataSearch()"
        />
      </div>

      <div class="viewer-panel__state">Matches: {{ viewer.enterpriseFilterResults.length }}</div>
      <select
        class="viewer-panel__select"
        [value]="viewer.enterpriseSelectedNodeId ?? ''"
        (change)="viewer.onEnterpriseSelectedNodeChanged($event)"
      >
        <option value="">Select matched node</option>
        <option *ngFor="let result of viewer.enterpriseFilterResults" [value]="result.id">
          {{ result.title }} | {{ result.attributeValue ?? 'n/a' }}
        </option>
      </select>

      <div class="viewer-panel__actions">
        <input
          type="button"
          class="demo-button viewer-panel__button"
          value="Open Matched Node"
          [disabled]="viewer.enterpriseSelectedNodeId === null"
          (tap)="viewer.openEnterpriseSelectedNode()"
        />
      </div>

      <div class="viewer-panel__state">External links in selected node:</div>
      <div class="viewer-panel__hint" *ngIf="viewer.getSelectedNodeExternalLinks().length === 0">
        No links detected in attributes.
      </div>
      <div class="viewer-panel__attribute" *ngFor="let link of viewer.getSelectedNodeExternalLinks()">
        <div class="viewer-panel__attribute-name">{{ link.label }}</div>
        <a class="viewer-panel__link" [href]="link.url" target="_blank" rel="noopener noreferrer">{{ link.url }}</a>
      </div>
    </div>
  </div>
</div>

<ng-template #sceneTreeNodeTemplate let-node>
  <div
    class="tree-node"
    [attr.data-scene-node-id]="node.id"
    [class.tree-node--selected]="viewer.selectedSceneNodeId === node.id"
    [style.padding-left.px]="node.level * 10"
  >
    <button
      class="tree-node__toggle"
      (click)="viewer.toggleSceneTreeNode(node)"
      [disabled]="!node.hasChildren || node.childrenLoading"
    >
      {{ node.hasChildren ? (node.isExpanded ? '-' : '+') : '·' }}
    </button>
    <button class="tree-node__title" (click)="viewer.selectSceneTreeNode(node)">
      {{ node.title }}
    </button>
    <span class="tree-node__meta">L{{ node.level }} / {{ node.allChildrenCount }}</span>
    <span class="tree-node__actions" role="group" [attr.aria-label]="'Actions for ' + node.title">
      <button
        type="button"
        class="tree-node__action"
        [class.tree-node__action--active]="node.visibility === 'visible'"
        [class.tree-node__action--hidden]="node.visibility === 'hidden'"
        [class.tree-node__action--mixed]="node.visibility === 'mixed'"
        [attr.title]="node.visibility === 'hidden' ? 'Unhide element and children' : 'Hide element and children'"
        [attr.aria-label]="
        (node.visibility === 'hidden' ? 'Unhide ' : 'Hide ') + node.title + ' and children'
      "
        (click)="viewer.toggleSceneTreeNodeVisibility($event, node)"
      >
        <svg class="tree-node__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"></path>
          <circle cx="12" cy="12" r="2.5"></circle>
          <path *ngIf="node.visibility === 'hidden' || node.visibility === 'mixed'" d="m4 4 16 16"></path>
        </svg>
      </button>
      <button
        type="button"
        class="tree-node__action"
        title="Focus element in 3D"
        [attr.aria-label]="'Focus ' + node.title + ' in 3D'"
        (click)="viewer.runSceneTreeNodeAction($event, node, 'focus')"
      >
        <svg class="tree-node__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="5"></circle>
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4"></path>
        </svg>
      </button>
    </span>
  </div>

  <div class="tree-node__children" *ngIf="node.isExpanded">
    <ng-container *ngFor="let childNode of node.children">
      <ng-container *ngTemplateOutlet="sceneTreeNodeTemplate; context: { $implicit: childNode }"></ng-container>
    </ng-container>
  </div>
</ng-template>
